import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { useTranslation } from 'react-i18next';
import Spring from '@/components/Spring';
import LegendItem from '@/ui/LegendItem';
import AnimatedCount from '@/components/AnimatedCount';
import styles from './styles.module.scss';

interface PredictionStatsProps {
  stats?: {
    exact: number;
    partial: number;
    miss: number;
  };
  tournamentId?: number;
}

const COLORS = {
  exact: 'var(--green, #22c55e)',
  partial: 'var(--yellow, #eab308)',
  miss: 'var(--gray, #6b7280)'
};

const CustomTooltip = ({ active, payload }: any) => {
  const { t } = useTranslation('tournament');
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className={styles.tooltip}>
        <span className={styles.tooltipName}>{data.name}</span>
        <span className={styles.tooltipValue}>{data.value} {t('predictions').toLowerCase()}</span>
      </div>
    );
  }
  return null;
};

const PredictionStats: React.FC<PredictionStatsProps> = ({
  stats = { exact: 12, partial: 18, miss: 8 }
}) => {
  const { t } = useTranslation('tournament');
  const total = stats.exact + stats.partial + stats.miss;

  const chartData = [
    { name: t('exactScoreLabel'), value: stats.exact, color: COLORS.exact, key: 'green' },
    { name: t('partial'), value: stats.partial, color: COLORS.partial, key: 'yellow' },
    { name: t('missed'), value: stats.miss, color: COLORS.miss, key: 'gray' }
  ];

  const getPercentage = (value: number) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <Spring className="card">
      <div className={styles.header}>
        <h3 className={styles.title}>{t('predictionStats')}</h3>
      </div>

      <div className={styles.chart}>
        <ResponsiveContainer width="100%" height={200}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              innerRadius={50}
              outerRadius={80}
              dataKey="value"
              stroke="var(--widget)"
              strokeWidth={3}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        <div className={styles.centerLabel}>
          <span className={styles.centerValue}>
            <AnimatedCount count={total} />
          </span>
          <span className={styles.centerText}>{t('total')}</span>
        </div>
      </div>

      <div className={styles.legend}>
        {chartData.map((item) => (
          <div key={item.name} className={styles.legendRow}>
            <LegendItem color={item.key} text={item.name} />
            <span className={styles.legendValue}>
              {getPercentage(item.value)}%
            </span>
          </div>
        ))}
      </div>
    </Spring>
  );
};

export default PredictionStats;
